import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { AsyncPipe } from '@angular/common';
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { Button } from 'primeng/button';
import { InputNumberModule } from 'primeng/inputnumber';
import { RelatorioContainer } from '@/shared/components/relatorios/components/relatorio-container/relatorio-container';
import { RelatorioFormBase } from '@/shared/components/relatorios/relatorio-form.base';
import { RelatorioJobFacade } from '@/shared/components/relatorios/relatorio-job-base';
import { ReciboProtocoloPjService } from '@/core/api/sicom-api/registro/prestador/relatorios/recibo-protocolo-pj/recibo-protocolo-pj.service';
import { ReportReciboProtocoloPjRequest, ReportReciboProtocoloPjResponse } from '@/core/api/sicom-api/registro/prestador/relatorios/recibo-protocolo-pj/recibo-protocolo-pj.model';
import { ReciboProtocoloPjJobService } from '@/core/services/recibo-protocolo-pj-job.service';
import { CanDirective } from '@/core/permissions/can.directive';
import { PERMISSOES } from '@/core/permissions/permissoes.catalog';

type Req = ReportReciboProtocoloPjRequest;
type Resp = ReportReciboProtocoloPjResponse;

/** Controles tipados do formulário do Recibo de Protocolo. */
interface ReciboProtocoloPjFormControls {
    nuProtocolo: FormControl<number | null>;
    nuAno: FormControl<number | null>;
    tipoProtocolo: FormControl<number | null>;
}

@Component({
    selector: 'app-recibo-protocolo-pj',
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [AsyncPipe, Button, ReactiveFormsModule, InputNumberModule, RelatorioContainer, CanDirective],
    templateUrl: './recibo-protocolo-pj.html',
    styleUrl: './recibo-protocolo-pj.scss'
})
/**
 * Relatório PDF assíncrono: Recibo de Protocolo.
 *
 * Herda `RelatorioFormBase`: export/polling/download/erro vêm da base. Diferente dos demais
 * relatórios PJ, este documento é baseado em PROTOCOLO (não no prestador): NÃO usa
 * `PessoaService`. Os dados vêm de um formulário reativo tipado com número do protocolo + ano
 * (+ tipo de protocolo opcional, default 1 = Inscrição Pré-Prestador).
 */
export class ReportReciboProtocoloPjComponent extends RelatorioFormBase<Req, Resp> {
    /** Gate do botao Gerar: o backend exige esta role em /reports/** do modulo. */
    protected override readonly roleGerar = PERMISSOES.prestadores.relatorios.gerar;

    private readonly formBuilder = inject(FormBuilder);
    private readonly reciboService = inject(ReciboProtocoloPjService);
    readonly reciboJobService = inject(ReciboProtocoloPjJobService);

    /** Stream do job para o `app-relatorio-container` (preview/PDF) no template. */
    readonly job$ = this.reciboJobService.job$;

    form: FormGroup<ReciboProtocoloPjFormControls> = this.formBuilder.group<ReciboProtocoloPjFormControls>({
        nuProtocolo: new FormControl<number | null>(null, [Validators.required, Validators.min(1)]),
        nuAno: new FormControl<number | null>(new Date().getFullYear(), [Validators.required, Validators.min(1900), Validators.max(9999)]),
        tipoProtocolo: new FormControl<number | null>(1, [Validators.min(1)])
    });

    protected get jobFacade(): RelatorioJobFacade {
        return this.reciboJobService;
    }

    /** Monta o request a partir do formulário; retorna null se o formulário for inválido. */
    protected buildRequest(): Req | null {
        this.form.markAllAsTouched();
        if (this.form.invalid) return null;

        const { nuProtocolo, nuAno, tipoProtocolo } = this.form.getRawValue();
        return {
            nuProtocolo: nuProtocolo as number,
            nuAno: nuAno as number,
            tipoProtocolo: tipoProtocolo ?? 1
        };
    }

    protected exportar(request: Req) {
        return this.reciboService.exportarPdf(request);
    }

    limparFormulario(): void {
        this.form.reset({ nuProtocolo: null, nuAno: new Date().getFullYear(), tipoProtocolo: 1 });
        this.limparJob();
    }
}
